import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Box, Text } from "grommet"
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Route, Routes } from 'react-router-dom'
import Layout from './common/Layout'
import { ErrorBoundary } from 'react-error-boundary'
import { useIdleTimer } from 'react-idle-timer'
import ShowLoading from './common/ShowLoading'
import { ShowInit, readAccessScope } from './common/Init'

import Error from './common/Error'

import link, { websocket_connected } from './common/ControlClient'

import { ApolloProvider, ApolloClient, gql, useMutation, useQuery } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache';

import TitlebarTokenService from 'titlebarTokenService';
import { UserProvider } from '../context/UserContext'

import { ReactComponent as TTLogo } from '../assets/logo-tt-full.svg';
import envConfig from '../utils/envConfig'
import { useWarnVersion } from '../utils/warnVersion'

import StackTrace from "stacktrace-js";
import { THIRTY_SECONDS } from '../utils/pollIntervals';


// Split with Webpack
// https://blog.logrocket.com/speed-up-react-app-dynamic-imports-route-centric-code-splitting/
// https://reactjs.org/docs/code-splitting.html#route-based-code-splitting

const Home = lazy(() => import('./Home'))
const Data = lazy(() => import('./data/Data'))
const User = lazy(() => import('./User'))
const Health2 = lazy(() => import('./health2/Health'))
const Admin = lazy(() => import('./admin/Admin'))
const Market = lazy(() => import('./market/Market'))
const Triage = lazy(() => import('./triage/Triage'))
const Alert = lazy(() => import('./alert/Alert'))
const Products = lazy(() => import('./pds/Products'))
const Bouncer = lazy(() => import('./bouncer/Bouncer'))
const Notifications = lazy(() => import('./notification/Notification'))
const Sessions = lazy(() => import('./sessions/Sessions'))
const FIX = lazy(() => import('./fix/FixHome'))
const AWS = lazy(() => import('./aws/AWS'))


const InstrumentViewPublic = lazy(() => import('./pds/InstrumentViewPublic'))
const TriageResultPublicView = lazy(() => import('./triage/jobtask/recent/TriageResultPublicView'))
const TriageHistoryPublicView = lazy(() => import('./triage/jobtask/history/TriageHistoryPublicView'))
const Actions = lazy(() => import('./actions/Actions'))
const FIXGWOrders = lazy(() => import('./fixgworders/FIXGWOrders'))
const Observability = lazy(() => import('./observability/Observability'))

const ServiceHistoryPublic = lazy(() => import('./history/ServiceHistoryPublic'))
const StandaloneHistoryV2 = lazy(() => import('./history/v2/StandaloneHistory'))

const SmokeTest = lazy(() => import('./smoketest/SmokeTest'))

const NoActivityTimeOut = 30 // Minutes

const ttidUrl = envConfig[process.env.REACT_APP_ENVIRONMENT]['titlebar_url']
const tokenEnv = envConfig[process.env.REACT_APP_ENVIRONMENT]['default_env']
const tokenService = new TitlebarTokenService({
  url: ttidUrl,
  cookie: document.cookie,
  environment: tokenEnv
});

/**
 * Used to determine if we hold a valid token currently.
 * Similar to what titlebar does in its token receive failed handler.
 * https://github.com/tradingtechnologies/titlebar-api/commit/0c184453450a056d92ea573caf15a7e94e741769
 *
 * @returns {boolean} evaluates the current expiration of the token we currently hold.
 */
function haveValidToken(tokenData) {
  return tokenData && Object.keys(tokenData).length > 0 && tokenData.expirationEpochMs > (new Date()).getTime();
}


// Register all community features
ModuleRegistry.registerModules([AllCommunityModule]);

const ShowIdle = () => (
  <Box fill background="light-3" justify="center" align="center">
    <Box align="center" justify="start" alignContent="start" pad="none" gap="none" margin="none">
      <Text weight="bold" color="dark-3" size="xlarge">
        No user activity have been detected for the last {NoActivityTimeOut} minutes
      </Text>
      <Text weight="bold" color="brand" size="small">
        Suspending Websocket Connections Until Activity Detected
      </Text>
      <TTLogo />
    </Box>
  </Box>
)

const App = () => {

  const [tokenData, setTokenData] = useState({})

  tokenService.on('token-received', async function (e, token) {
    setTokenData(token)
    console.log('Token Received');
  });

  // handle cookie update properly like ttw
  //https://github.com/search?q=repo%3Atradingtechnologies%2FNexTraderWeb%20UpdateCookie&type=code
  // tokenService.on('update-cookie', function (e, cookie) {
  //   document.cookie = cookie;
  //   console.log('Cookie Updated');
  // });

  // handle token expiring like ttw
  // https://github.com/tradingtechnologies/NexTraderWeb/pull/13153/files
  // actually this means that the fetch of the token from TB server (SSO) fails its good to log it for debug
  // https://github.com/tradingtechnologies/titlebar-lib/blob/2118e511e5fdb99722901e8aba4798d146e3cc5e/js/token-handler.js#L934-L980
  tokenService.on('token-expiring', function (e, response) {
    const { failureReason, userMessage } = response;
    console.warn(`token-manager: token expiring reason=${failureReason} message=${userMessage}`);
  });

  // handle token receive failed like ttw
  // https://github.com/search?q=repo%3Atradingtechnologies%2FNexTraderWeb+onTokenReceiveFailed&type=code
  tokenService.on('token-receive-failed', function () {
    // If we have a valid token and not expired yet, we will not abandon it
    if (!haveValidToken(tokenData)) {
      console.log('Error retrieving the Token.. Redirecting to login page...');
      window.location.href = ttidUrl + "titlebar/login/?&redirectUri=" + window.location.href;
    } else {
      console.log(`Error retrieving the Token... Existing token is still valid, continuing until expiration at ${new Date(tokenData.expirationEpochMs).toLocaleString()}...`);
    }
  });

  tokenService.startRefreshingToken();

  const handleLogout = () => {
    setTokenData({})
    tokenService.logOut()
    console.log("Logging out...")
    window.location.reload()
  }

  // https://www.apollographql.com/docs/react/caching/cache-configuration/#data-normalization
  const cache = new InMemoryCache({
    typePolicies: {
      service_node: {
        keyFields: ["instance"],
      },
      node: {
        keyFields: ["name"],
      },
      healthstatus: {
        keyFields: ["env"],
      },
      serviceGroup: {
        keyFields: ["group"],
      },
      ServiceNodeView: {
        keyFields: ["instance"]
      },
      serviceRecentUpView: {
        keyFields: ["instance", "environmentName"]
      },
    },
  });

  const client = new ApolloClient({
    link,
    cache
  })

  // Check for Activity
  const [isActive, setIsActive] = useState(true)

  // Check Version
  const [isNewVersion, setisNewVersion] = useState(false)

  const handleOnIdle = event => {

    // If there is newVesion then reload first
    if (isNewVersion) {
      window.location.reload();
    }

    console.log('user is idle', event)
    console.log('last active', getLastActiveTime())
    setIsActive(false)
  }

  const handleOnActive = event => {
    console.log('user is active', event)
    console.log('time remaining', getRemainingTime())
    setIsActive(true)
  }

  const handleOnAction = (e) => {
    setIsActive(true)
  }

  const { getRemainingTime, getLastActiveTime } = useIdleTimer({
    timeout: 1000 * 60 * NoActivityTimeOut,
    onIdle: handleOnIdle,
    onActive: handleOnActive,
    onAction: handleOnAction,
    debounce: 500
  })

  if (Object.keys(tokenData).length > 0 && tokenData.scopes.indexOf(readAccessScope) !== -1) {
    if (!isActive) {
      return (
        <ShowIdle />
      )
    } else {
      return (
        <ApolloProvider client={client}>
          <AppLauncher token={tokenData} handleLogout={handleLogout} setisNewVersion={setisNewVersion} />
        </ApolloProvider>
      )
    }
  } else {
    return (
      <ShowInit
        titlebarCheck={(tokenData && Object.keys(tokenData).length > 0) ? true : false}
        puserCheck="loading"
        personId={tokenData && tokenData.personId}
        scopes={tokenData && tokenData.scopes}
      />
    )
  }

}


const PUSER_QUERY = gql`
query ($personId: bigint!, $titlebar: String!) {
  puser(where: {personId: {_eq: $personId}, titlebar: {_eq: $titlebar}}) {
    id
    personId
    titlebar
    firstName
    lastName
    email
    status
    timezone
    theme
    notifications
  }
}
`

const ADD_STACKTRACE = gql`
mutation(
	$email: String!
	$trace: jsonb!
	$version: String!
	$url: String!
	$info: jsonb!
) {
	insert_errorReporting(
		objects: {
			email: $email
			stackTrace: $trace
			version: $version
			url: $url
			info: $info
		}
		on_conflict: {
			constraint: errorReporting_email_stackTrace_url_key
			update_columns: email
		}
	) {
		affected_rows
	}
}

`

const AppLauncher = ({ token, handleLogout, setisNewVersion }) => {

  // Check Version if New and Pass it Back
  const warnVersion = useWarnVersion()
  useEffect(() => {
    if (warnVersion) {
      setisNewVersion(warnVersion.warnVersion)
    }
  }, [warnVersion, setisNewVersion])

  const ttidName = envConfig[process.env.REACT_APP_ENVIRONMENT]['titlebar_name']
  const liteMode = envConfig[process.env.REACT_APP_ENVIRONMENT]['lite']

  const [addStacktraceUS] = useMutation(ADD_STACKTRACE, { context: { backend: 'us' } })
  const [addStacktraceEU] = useMutation(ADD_STACKTRACE, { context: { backend: 'eu' } })

  if (ttidName === 'local-prod' || ttidName === 'local-lite') {
    token.personId = -1
  }

  const [enrichedToken, setEnrichedToken] = useState({})

  const { data, loading, error, networkStatus } = useQuery(PUSER_QUERY, {
    variables: { personId: token.personId, titlebar: ttidName }, pollInterval: THIRTY_SECONDS
  })

  const [errorDetails, seterrorDetails] = useState({})

  // Handling Crashes
  const myErrorHandler = (error, info) => {
    // Do something with the error

    // https://stackoverflow.com/questions/50223991/how-to-make-react-create-app-production-error-boundary-map-to-source-code
    StackTrace.fromError(error).then(err => {

      const errorJson = JSON.stringify(err)
      const errorJsonMsg = JSON.stringify(error, Object.getOwnPropertyNames(error))

      seterrorDetails({
        errorJson: errorJson,
        errorJsonMsg: errorJsonMsg
      })

      if (!process.env.REACT_APP_ENVIRONMENT.startsWith('local')) {
        addStacktraceUS(
          {
            variables:
            {
              email: enrichedToken.user_email,
              trace: errorJson,
              version: process.env.REACT_APP_VERSION,
              info: errorJsonMsg,
              url: window.location.href
            }
          }
        )
        addStacktraceEU(
          {
            variables:
            {
              email: enrichedToken.user_email,
              trace: errorJson,
              version: process.env.REACT_APP_VERSION,
              info: errorJsonMsg,
              url: window.location.href
            }
          }
        )
      }

    });

  }

  function ErrorFallback() {
    return (
      <Error errno="500"
        stackerror={errorDetails}
      />
    )
  }

  useEffect(() => {
    if (data && data.puser && data.puser.length > 0) {

      // Determine Timezone
      const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

      // If timezone empty fall back to default timezone
      let timezone = data.puser[0].timezone
      if (!liteMode) {
        timezone = timezone ? timezone : (localStorage.getItem("timezone") ? localStorage.getItem("timezone") : defaultTimezone)
      } else {
        timezone = localStorage.getItem("timezone") ? localStorage.getItem("timezone") : "UTC"
      }


      setEnrichedToken({
        ...token,
        'user_email': data.puser[0].email,
        'first_name': data.puser[0].firstName,
        'last_name': data.puser[0].lastName,
        'status': data.puser[0].status,
        'timezone': timezone,
        'theme': data.puser[0].theme,
        'notifications': data.puser[0].notifications
      })
      localStorage.setItem("theme", data.puser[0].theme);
      localStorage.setItem("timezone", timezone);

      // For Notification
      sessionStorage.setItem("notifyMessages", JSON.stringify([]));
      sessionStorage.setItem("currentDownNodes", JSON.stringify([]));
    }
  }, [data, loading, token, liteMode])

  /*
   *  PLEASE TAKE NOTE OF THE FOLLOWING WHILE ADDING ROUTES
   *  1. If you intend to add multiple paths to the same component
   *         Example: /triage/:tab and /triage/:tab/:appName
   *     Please ALWAYS remember that REACT will route logic from TOP to BOTTOM, meaning you will need
   *     to place the sub-paths to be higher than the main path, so in this context
   *         Example:
   *            <Route path="/triage/:tab/:appName/:commandName" element={<Triage/>} />
   *            <Route path="/triage/:tab" element={<Triage/>} />
   *
   *   2. Routes cannot be encompassed within <> as this will result in the
   *      routes being combined together as a set, resulting in unexpected path
   */

  if (error) {
    console.warn("Error Fetching Enriched Token Data From CC Database: ", error)
  }

  if (Object.keys(enrichedToken).length > 0 && token.scopes.indexOf(readAccessScope) !== -1) {
    return (
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={myErrorHandler}
      >
        <UserProvider value={enrichedToken}>
          <Layout handleLogout={handleLogout}>
            <Suspense fallback={<ShowLoading />}>
              <Routes>
                <Route path="/" exact element={<Home />} />

                {/* Components on Single Route */}
                <Route path="/alert" element={<Alert />} />
                <Route path="/aws/:tab" element={<AWS />} />
                <Route path="/fixgworders/:tab" element={<FIXGWOrders />} />
                <Route path="/bouncer/:tab" element={<Bouncer />} />
                <Route path="/data/:tab" element={<Data />} />
                <Route path="/fix/:tab" element={<FIX />} />
                <Route path="/instrumentview/:instrument_id" element={<InstrumentViewPublic />} />
                <Route path="/products/:tab" element={<Products />} />

                <Route path="/sessions/:tab" element={<Sessions />} />
                <Route path="/user" element={<User />} />

                <Route path="/sre/actions" element={<Actions />} />
                <Route path="/observability" element={<Observability />} />

                {/* Components on Multiple Routes */}
                <Route path="/admin/:tab/:sub1" element={<Admin />} />
                <Route path="/admin/:tab" element={<Admin />} />

                <Route path="/health/:tab/:serviceGroupName/chef/:nodeName" element={<Health2 />} />
                <Route path="/health/:tab/:serviceGroupName/:instance" element={<Health2 />} />
                <Route path="/health/:tab/:serviceGroupName" element={<Health2 />} />
                <Route path="/health/:tab" element={<Health2 />} />

                <Route path="/market/:tab/:sub1" element={<Market />} />
                <Route path="/market/:tab" element={<Market />} />


                <Route path="/servicehistory/:host/:service/:timestamp/:instance" element={<ServiceHistoryPublic />} />
                <Route path="/zkhistory" element={<StandaloneHistoryV2 />} />

                {!liteMode &&
                  <Route path="/notifications/:tab" element={<Notifications />} />
                }
                {!liteMode &&
                  <Route path="/triage/:tab" element={<Triage />} />
                }
                {!liteMode &&
                  <Route path="/triage/showtask/:taskid" element={<TriageResultPublicView />} />
                }
                {!liteMode &&
                  <Route path="/triage/triagetask/:taskid" element={<TriageHistoryPublicView />} />
                }
                {!liteMode &&
                  <Route path="/smoketest/:tab" element={<SmokeTest />} />
                }

                <Route path="*" render={() => <Error errno="404" />} />
              </Routes>
            </Suspense>
          </Layout>
        </UserProvider>
      </ErrorBoundary>
    )
  } else {
    return (
      <ShowInit
        titlebarCheck={token ? true : false}
        puserCheck={loading ? "loading" : (!loading && data && data.puser && data.puser.length > 0 ? true : false)}
        personId={token && token.personId}
        scopes={token && token.scopes}
        gqlCheck={!networkStatus ? "loading" : (networkStatus === 8 ? false : (websocket_connected ? true : false))}
      />
    )
  }
}

export default App
